import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import Select from "react-select"
import { Box, Grid, Stack, TextField, Typography, Card, IconButton, Tooltip, InputAdornment, CircularProgress, FormControlLabel, Switch, } from '@mui/material';
import MDButton from 'components/MDButton';
import { grey } from '@mui/material/colors';
// icons
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import AngleRightIcon from "common/customIcons/angle_right";
import DeleteIcon from "common/customIcons/delete";
// utils and helpers
import { handleUploadFile } from "services/fileUpload";
import { validateSubRegionData } from "validations/hyperwolf/regions/subRegion";
import { addSubRegion, getBlazeRegions, getSubRegionDetails, updateSubRegion } from "../../../../redux/slices/hyperwolf/settings";
import { openSnackbar } from "../../../../redux/slices/common";
import { showToastMsg } from "components/distributionSetting/distributionManagement/addRegionKit";
// custom components
import CommonLayout from "layouts/DashboardLayout";
import MapWithPolygon from "components/googleMaps"

export const circularProgressIcon = {
  backgroundColor: '#fff',
  borderRadius: 10,
  p: .5,
  position: 'absolute'
};

const AddHyperwolfSubRegion = () => {
  const { subRegionId } = useParams()
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const [details, setDetails] = useState({ status: true });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [blazeRegions, setBlazeRegions] = useState([]);

  const handleBlazeRegions = async () => {
    try {
      const response = await dispatch(getBlazeRegions()).unwrap();
      if (response?.data) {
        const { regions } = response?.data || {};
        setBlazeRegions(regions || []);
      }
    } catch (error) {
      showToastMsg(error, "error");
    }
  }

  const handleSubRegionDetails = async () => {
    try {
      const response = await dispatch(getSubRegionDetails(subRegionId)).unwrap()
      if (response?.data) {
        const { name, status, kmls, _id, blazeRegionId } = response?.data || {}
        setDetails({ name, status, kmls, _id, blazeRegionId })
      }
    } catch (error) {
      const errorMsg = error?.response?.data?.message || error?.message || error
      dispatch(openSnackbar({ content: errorMsg, color: "error", icon: "warning", }));
    }
  }

  const handleChange = (event) => {
    const { name, value } = event.target;

    setDetails(prev => ({ ...prev, [name]: value }))
  };

  const handleStatusChange = (tab) => {
    setDetails(prev => ({ ...prev, status: tab }))
  }

  const handleFileUpload = async (e) => {
    try {
      setIsLoading(true);

      const { files } = e.target
      const file = files[0]

      const isKmlMimeType = file?.type && file?.type?.toLowerCase()?.includes("kml");
      const isKmlExtension = file?.name && file?.name?.toLowerCase()?.endsWith(".kml");

      if (!isKmlMimeType && !isKmlExtension) {
        dispatch(openSnackbar({ content: "Upload a valid KML file", color: "error", icon: "warning", }));
        return;
      }
      const formData = new FormData()
      formData.append("filePath", file)

      const fileResponse = await handleUploadFile(formData)
      if (fileResponse?.fileUrl) {
        setDetails(prev => ({
          ...prev,
          kmls: [
            ...(prev.kmls || [])?.map(kml => ({ ...kml, active: false })),
            { url: fileResponse?.fileUrl, filename: file?.name, active: true }
          ]
        }))
      }
    }
    catch (error) {
      showToastMsg(error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleChangeFileName = (e, kml) => {
    const { name, value, checked } = e.target;

    setDetails(prev => {
      const updateKmlIndex = (prev.kmls || []).findIndex(item => item.url === kml.url);
      let upadtedKmls = [...prev.kmls];

      if (name === "active") {
        upadtedKmls = upadtedKmls.map(kml => ({ ...kml, active: false }));
        upadtedKmls[updateKmlIndex] = { ...kml, [name]: checked };
      } else {
        upadtedKmls[updateKmlIndex] = { ...kml, [name]: value };
      }

      return { ...prev, kmls: upadtedKmls };
    });
  }

  const handleDownloadPDF = async (url) => {
    if (!url) return;
    try {
      const link = document.createElement('a');
      link.href = url;
      link.download = "kml.pdf";
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
      }, 100);
    }
    catch (error) {
      showToastMsg(error, "error");
    }
  };

  const handleSave = async () => {
    try {
      if (isLoading) return;
      setIsLoading(true);

      const { isValid, myErrors } = validateSubRegionData(details);
      if (!isValid) {
        setErrors(myErrors)
        return;
      } else {
        setErrors({})
      }

      const blazeRegionName = blazeRegions?.find(s => s.id === details?.blazeRegionId)?.name;

      let response = {}
      if (subRegionId) {
        response = await dispatch(updateSubRegion({ ...details, blazeRegionName })).unwrap();
      } else {
        response = await dispatch(addSubRegion({ ...details, blazeRegionName })).unwrap();
      }

      if (response?._id || response?.message) {
        showToastMsg(response?.message || "Success", "success")
        navigate("/regions?tab=subRegions")
      }

    } catch (error) {
      const errorMsg = error?.response?.data?.message || error?.message || error
      dispatch(openSnackbar({ content: errorMsg, color: "error", icon: "warning", }));
    } finally {
      setIsLoading(false)
    }
  };

  useEffect(() => {
    if (subRegionId) {
      handleSubRegionDetails()
    }
  }, [subRegionId]);

  useEffect(() => {
    handleBlazeRegions();
  }, [])

  return (
    <CommonLayout>
      <Box p={{ xs: 2, sm: 3, lg: 4 }} className={subRegionId ? "" : "cus-region-kit"}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ sm: "center" }}
          justifyContent="space-between"
          gap={2}
          mb={2}
        >
          <Box display="flex" alignItems="center" color="text.main" lineHeight={1}>
            <IconButton onClick={() => navigate(-1)}>
              <AngleRightIcon fontSize="14px" />
            </IconButton>
            <Typography variant="body2" fontWeight="semibold" ml={0.75}>
              Sub Region
            </Typography>
          </Box>
        </Stack>

        <Grid container spacing={3}>
          <Grid item xs={12} md={subRegionId ? 7 : 12}>
            <Card sx={{ p: { xs: 2, md: 3 }, borderRadius: 2.5, boxShadow: "none" }}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" gap={.75}>
                    <Typography variant="h5" fontWeight="semibold">{subRegionId ? "Update Sub Region" : "Add Sub Region"}</Typography>
                    <Box display="flex" alignItems="center" gap={1.25}>
                      <Typography variant='h5' fontWeight="semibold" color="secondary">Status</Typography>
                      <Stack direction="row" alignItems="center" gap={.75} lineHeight={1} p={.75} borderRadius={2} bgcolor="#F2F3F7">
                        <MDButton
                          sx={{ minWidth: '92px', borderRadius: 1.75, minHeight: '28px', p: .25, fontWeight: 500 }}
                          type='button'
                          variant={details.status ? 'contained' : 'text'}
                          color={details.status ? 'success' : 'dark'}
                          size="small"
                          onClick={() => handleStatusChange(true)}
                        >
                          Active
                        </MDButton>
                        <MDButton
                          sx={{ minWidth: '92px', borderRadius: 1.75, minHeight: '28px', p: .25, fontWeight: 500 }}
                          type='button'
                          variant={!details.status ? 'contained' : 'text'}
                          color={!details.status ? 'error' : 'dark'}
                          size="small"
                          onClick={() => handleStatusChange(false)}
                        >
                          Inactive
                        </MDButton>
                      </Stack>
                    </Box>
                  </Stack>
                </Grid>

                {/* Sub region name */}
                <Grid item xs={12}>
                  <Typography variant='body2' fontWeight="medium" color={grey[600]} mb={1}>Sub Region</Typography>
                  <TextField
                    placeholder="Sub Region"
                    fullWidth
                    variant="outlined"
                    className="form-input bg-transparent"
                    name="name"
                    value={details.name}
                    onChange={handleChange}
                  />
                  {errors.name && <p className="error">{errors.name}</p>}
                </Grid>

                {/* Sub region name */}
                <Grid item xs={12}>
                  <Typography variant='body2' fontWeight="medium" color={grey[600]} mb={1}>Blaze Region</Typography>
                  <Select
                    className='cus-react-select white-radius-field'
                    classNamePrefix="react-select"
                    placeholder="Select Blaze Region"
                    components={{ IndicatorSeparator: null }}
                    options={blazeRegions}
                    getOptionValue={option => option.id}
                    getOptionLabel={option => option.name}
                    name="blazeRegionId"
                    value={blazeRegions?.find(s => s.id === details?.blazeRegionId)}
                    onChange={option => handleChange({ target: { name: "blazeRegionId", value: option ? option.id : "" } })}
                  />
                  {errors.blazeRegionId && <p className="error">{errors.blazeRegionId}</p>}
                </Grid>

                {/* Upload KML */}
                <Grid item xs={12}>
                  <Typography variant='body2' fontWeight="medium" color={grey[600]} mb={1}>Upload KML</Typography>
                  <Stack direction="row" flexWrap="wrap" gap={2} mb={2} alignItems="center">
                    <label htmlFor='product-image-input'>
                      <Box
                        position="relative"
                        bgcolor="#F1F1F1"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        flexDirection="column"
                        minWidth="160px"
                        minHeight="160px"
                        borderRadius="20px"
                        p={2}
                        className="cur-pointer"
                      >
                        {
                          isLoading
                            ? <CircularProgress size={32} color='primary' sx={circularProgressIcon} />
                            : <Typography variant='body2' fontWeight="medium" textAlign="center">+<br /> Upload KML</Typography>
                        }
                      </Box>
                    </label>

                    <input
                      id="product-image-input"
                      type="file"
                      name="url"
                      onChange={handleFileUpload}
                      disabled={isLoading}
                      accept='.kml'
                      style={{ display: 'none' }}
                    />

                    {details?.kml && !subRegionId &&
                      <Card sx={{ overflow: "hidden", borderRadius: 2.5, boxShadow: "none", width: "160px", height: "160px" }} className=''>
                        <MapWithPolygon kmlUrl={details?.kml} />
                      </Card>
                    }
                  </Stack>

                  {details?.kmls?.length > 0 && <Box className="uploaded-kml-files">
                    <Typography variant='body2' fontWeight="medium" color={grey[600]} mb={1}>Uploaded KML Files</Typography>
                    <Box style={{ background: "#f6f6f6", borderRadius: "12px" }}>
                      {details?.kmls?.map(kml => (
                        <Box key={kml.url} display='flex' justifyContent='space-between' gap={2} className="xml-upload-rep">
                          <Box display='flex' alignItems='center' style={{ minWidth: "50px" }}>
                            <FormControlLabel
                              name='active'
                              control={<Switch
                                name="active"
                                checked={kml?.active}
                                onChange={e => {
                                  handleChangeFileName(e, kml)
                                }}
                              />}
                              sx={{ m: 0 }}
                            />
                          </Box>
                          <Tooltip title={details.kml} sx={{ whiteSpace: 'pre-wrap', }} arrow placement="bottom">
                            <TextField
                              fullWidth
                              disabled={isLoading}
                              className="form-input-upload"
                              name="filename"
                              value={kml?.filename || ""}
                              onChange={e => handleChangeFileName(e, kml)}
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end" className="upload-icons">
                                    <IconButton
                                      color='secondary'
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDownloadPDF(kml.url);
                                      }}>
                                      <FileDownloadOutlinedIcon color="#2E2E3A" />
                                    </IconButton>
                                    <IconButton
                                      color='secondary'
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setDetails(prev => ({ ...prev, kmls: prev.kmls.filter((item) => item.url !== kml.url) }));
                                      }}>
                                      <DeleteIcon color="#FD4438" width="16px" />
                                    </IconButton>
                                  </InputAdornment>
                                ),
                              }}
                            />
                          </Tooltip>
                        </Box>
                      ))}
                    </Box>
                  </Box>}

                  {errors?.kmls && <p className="error">{errors?.kmls}</p>}
                </Grid>

                {/* Action buttons */}
                <Grid item xs={12}>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mt={1}>
                    <MDButton
                      sx={{ minWidth: "160px" }}
                      type="button"
                      variant="contained"
                      color="primary"
                      disabled={isLoading}
                      onClick={handleSave}
                    >
                      Save
                    </MDButton>
                    <MDButton
                      sx={{ minWidth: "160px" }}
                      type="button"
                      variant="outlined"
                      color="dark"
                      onClick={() => navigate(-1)}
                    >
                      Cancel
                    </MDButton>
                  </Stack>
                </Grid>
              </Grid>
            </Card>
          </Grid>

          {/* Kml Map view */}
          {
            details?.kmls && subRegionId && <Grid item xs={12} md={5}>
              <Card sx={{ overflow: "hidden", borderRadius: 2.5, boxShadow: "none", height: "470px" }} className='w-100'>
                <MapWithPolygon kmlUrl={details?.kmls?.find(kml => kml?.active)?.url} />
              </Card>
            </Grid>
          }
        </Grid>
      </Box>
    </CommonLayout>
  )
}

export default AddHyperwolfSubRegion